import React, { useEffect, useState } from "react";
import { Settings2, XCircle, ArrowDownCircle, Layers } from "lucide-react";
import PlansDashboard from "./plans";
import AdvancedSettings from "./AdvancedSettings";
import CancellationSettings from "./CancellationSettings";
import DowngradeSettings from "./DowngradeSettings";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import { SaasSettingsType } from "./components/dataTypes";
import MainSaasLoader from "./components/skeletonLoaders/MainSaasLoader";
import { get } from "http";

const SaasConfigurator: React.FC = () => {
  const AppService = useAppServices();
  const { user } = useUserInfo();

  const [activeTab, setActiveTab] = useState<string>("plans");
  const [saadSettings, setSaadSettings] = useState<SaasSettingsType | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const tabs = [
    { id: "plans", label: "Plans", icon: Layers },
    { id: "advanced_settings", label: "Advanced Settings", icon: Settings2 },
    {
      id: "cancellation_settings",
      label: "Cancellation Settings",
      icon: XCircle,
    },
    {
      id: "downgrade_settings",
      label: "Downgrade Settings",
      icon: ArrowDownCircle,
    },
  ];

  const getSaasSettings = async () => {
    try {
      const { response } = await AppService.saas_setting.get({
        query: `companyId=${user?.companyId}`,
      });
      if (response) {
        setSaadSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch SaaS settings:", error);
      setSaadSettings(null);
    } finally {
      setIsLoading(false);
    }
  };
  let pageLoad = true;
  const onLoad = async () => {
    if (pageLoad) {
      getSaasSettings();
      pageLoad = false;
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <div className="card p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          SaaS Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage your plans and SaaS configuration
        </p>
      </div>

      {/* Tabs */}
      <div className="rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {isLoading ? (
            <MainSaasLoader />
          ) : (
            <>
              {activeTab === "plans" && <PlansDashboard />}
              {activeTab === "advanced_settings" && (
                <AdvancedSettings
                  saadSettings={saadSettings}
                  handleRefresh={onLoad}
                />
              )}
              {activeTab === "cancellation_settings" && (
                <CancellationSettings saadSettings={saadSettings} handleRefresh={onLoad}/>
              )}
              {activeTab === "downgrade_settings" && (
                <DowngradeSettings saadSettings={saadSettings} handleRefresh={onLoad} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaasConfigurator;
